import {Link, useParams} from 'react-router-dom';
import {UserCabinetNavbar} from '@common/ui/navigation/user-cabinet-navbar';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {message} from '@common/i18n/message';
import {Trans} from '@common/i18n/trans';
import {Button} from '@common/ui/buttons/button';
import {ColumnConfig} from '@common/datatable/column-config';
import {DataTable} from '@common/datatable/data-table';
import {FormattedDate} from '@common/i18n/formatted-date';
import {FormattedCurrency} from '@common/i18n/formatted-currency';
import {FormattedNumber} from '@common/i18n/formatted-number';
import {ArrowBackIcon} from '@common/icons/material/ArrowBack';
import {StorageIcon} from '@common/icons/material/Storage';
import {Tooltip} from '@common/ui/tooltip/tooltip';
import {LearnMoreLink} from '@common/admin/settings/learn-more-link';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';

const AI_TOKENS_AND_CACHE_DOCS_HREF = '/docs/guides/ai-tokens-and-cache';

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

const CREDIT_TX_SOURCE_TRANSLATION_KEYS = [
  'editor_chat',
  'tool_call',
  'studio_record',
  'credit_refund',
  'generate_image_tool',
  'generate_image_by_image_tool',
  'generate_motion_tool',
  'generate_speech_tool',
  'generate_speech_draft_tool',
  'jobs_motion',
  'jobs_talking_head',
  'jobs_talking_head2',
  'generate_clone_speech_tool',
  'clone_voice_tool',
  'get_image_prompt_tool',
  'transcribe_audio_tool',
  'top_up',
  'admin_manual_adjustment',
  'admin_top_up',
] as const;

const CREDIT_TX_SOURCE_MESSAGE_ALIASES: Record<string, string> = {
  generate_images_tool: 'credit_transactions.source.generate_image_tool',
};

const ISO_CURRENCY_RE = /^[A-Z]{3}$/;

export interface CreditTransactionDetailRow {
  id: string;
  uuid: string;
  source: string;
  model: string | null;
  credits_delta: number;
  tokens_total?: number | null;
  tool_count?: number;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

interface CreditTransactionsProjectDetailsResponse extends BackendResponse {
  kind?: 'site' | 'studio' | 'chat';
  project: {
    id: number;
    name: string | null;
    deleted: boolean;
    url?: string | null;
  };
}

const CHAT_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function detailsEndpoint(
  kind: 'site' | 'studio' | 'chat',
  projectId: number,
  chatId?: string,
): string {
  if (kind === 'chat' && chatId) {
    return `me/credit-transactions/chats/${chatId}`;
  }
  return kind === 'studio'
    ? `me/credit-transactions/studio-projects/${projectId}`
    : `me/credit-transactions/projects/${projectId}`;
}

function useCreditTransactionsProjectDetailsMeta(
  kind: 'site' | 'studio' | 'chat',
  projectId: number,
  chatId?: string,
) {
  return useQuery({
    queryKey: ['credit-transactions-project-meta', kind, projectId, chatId],
    queryFn: () =>
      apiClient
        .get(detailsEndpoint(kind, projectId, chatId), {
          params: {perPage: 5, page: 1},
        })
        .then(response => response.data as CreditTransactionsProjectDetailsResponse),
    enabled:
      kind === 'chat'
        ? Boolean(chatId && CHAT_UUID_RE.test(chatId))
        : Number.isInteger(projectId) && projectId > 0,
    staleTime: 30_000,
  });
}

function parseMetadataCostCurrency(
  metadata: CreditTransactionDetailRow['metadata'],
): {cost: number; currency: string} | null {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const rawCost = (metadata as Record<string, unknown>)['cost'];
  const rawCurrency = (metadata as Record<string, unknown>)['currency'];
  const cost =
    typeof rawCost === 'number'
      ? rawCost
      : typeof rawCost === 'string'
        ? Number.parseFloat(rawCost)
        : Number.NaN;
  if (!Number.isFinite(cost)) {
    return null;
  }
  const currency =
    typeof rawCurrency === 'string' ? rawCurrency.trim().toUpperCase() : '';
  if (!ISO_CURRENCY_RE.test(currency)) {
    return null;
  }
  return {cost, currency};
}

function CreditTransactionMetadataCost({row}: {row: CreditTransactionDetailRow}) {
  const parsed = parseMetadataCostCurrency(row.metadata);
  if (!parsed) {
    return <span className="text-muted">—</span>;
  }
  return <FormattedCurrency value={parsed.cost} currency={parsed.currency} />;
}

function formatCacheTokenCount(value: number): string {
  if (value < 1000) {
    return String(value);
  }
  if (value < 10000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return `${Math.round(value / 1000)}k`;
}

function parseMetadataCachedTokens(
  metadata: CreditTransactionDetailRow['metadata'],
): number | null {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(metadata, 'cached_tokens')) {
    return null;
  }
  const raw = (metadata as Record<string, unknown>)['cached_tokens'];
  const cached =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number.parseFloat(raw)
        : Number.NaN;
  if (!Number.isFinite(cached) || cached < 0) {
    return null;
  }
  return Math.floor(cached);
}

/**
 * Иконка cache hit/miss в детализации транзакций (как в AI-чате).
 * Добавлено: 2026-07-30.
 */
function CreditTransactionCacheIcon({row}: {row: CreditTransactionDetailRow}) {
  const cached = parseMetadataCachedTokens(row.metadata);
  if (cached === null) {
    return <span className="text-muted">—</span>;
  }
  const isHit = cached > 0;
  const total = Number(row.tokens_total ?? 0);
  const totalLabel = formatCacheTokenCount(Number.isFinite(total) ? total : 0);
  const cachedLabel = formatCacheTokenCount(cached);

  return (
    <Tooltip
      label={
        <Trans
          message={
            isHit
              ? 'Cache hit. Total: :total · Cached: :cached'
              : 'Cache miss. Total: :total · Cached: :cached'
          }
          values={{
            total: totalLabel,
            cached: cachedLabel,
          }}
        />
      }
      placement="top"
      delay={300}
    >
      <span
        className="inline-flex shrink-0 cursor-default"
        aria-label={
          isHit
            ? `Cache hit. Total: ${totalLabel}. Cached: ${cachedLabel}`
            : `Cache miss. Total: ${totalLabel}. Cached: ${cachedLabel}`
        }
      >
        {isHit ? (
          <StorageIcon size="xs" className="shrink-0 text-positive" />
        ) : (
          <span className="relative inline-flex shrink-0 text-muted">
            <StorageIcon size="xs" className="shrink-0 opacity-70" />
            <span
              className="pointer-events-none absolute left-1/2 top-1/2 block h-px w-[120%] -translate-x-1/2 -translate-y-1/2 rotate-[-45deg] bg-danger"
              aria-hidden
            />
          </span>
        )}
      </span>
    </Tooltip>
  );
}

function CreditTransactionSourceLabel({
  source,
  toolCount = 0,
}: {
  source: string;
  toolCount?: number;
}) {
  const aliasMessageKey = CREDIT_TX_SOURCE_MESSAGE_ALIASES[source];
  const hasKnownKey = (
    CREDIT_TX_SOURCE_TRANSLATION_KEYS as readonly string[]
  ).includes(source);
  const label = aliasMessageKey ? (
    <Trans {...message(aliasMessageKey)} />
  ) : hasKnownKey ? (
    <Trans {...message(`credit_transactions.source.${source}`)} />
  ) : (
    <span className="font-mono text-xs" title={source}>
      {source}
    </span>
  );
  if (source === 'editor_chat' && toolCount > 0) {
    return (
      <span>
        {label} ({toolCount})
      </span>
    );
  }
  return label;
}

const columns: ColumnConfig<CreditTransactionDetailRow>[] = [
  {
    key: 'uuid',
    allowsSorting: false,
    minWidth: 'min-w-150',
    header: () => <Trans message="Transaction UUID" />,
    body: row => (
      <span className="font-mono text-xs" title={row.uuid}>
        {row.uuid}
      </span>
    ),
  },
  {
    key: 'source',
    allowsSorting: false,
    header: () => <Trans message="Source" />,
    body: row => (
      <CreditTransactionSourceLabel
        source={row.source}
        toolCount={row.tool_count}
      />
    ),
  },
  {
    key: 'model',
    allowsSorting: false,
    header: () => <Trans message="Model" />,
    body: row =>
      row.model ? (
        <span className="font-mono text-xs">{row.model}</span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: 'metadata_cost',
    allowsSorting: false,
    minWidth: 'min-w-120',
    header: () => <Trans message="credit_transactions.column.usage_cost" />,
    body: row => <CreditTransactionMetadataCost row={row} />,
  },
  {
    key: 'cache',
    allowsSorting: false,
    header: () => <Trans message="credit_transactions.column.cache" />,
    body: row => <CreditTransactionCacheIcon row={row} />,
  },
  {
    key: 'credits_delta',
    allowsSorting: false,
    header: () => <Trans message="Credits delta" />,
    body: row => (
      <span className={row.credits_delta >= 0 ? 'text-positive' : 'text-danger'}>
        <FormattedNumber value={row.credits_delta} />
      </span>
    ),
  },
  {
    key: 'created_at',
    allowsSorting: false,
    header: () => <Trans message="Created at" />,
    body: row =>
      row.created_at ? (
        <FormattedDate date={row.created_at} options={dateTimeOptions} />
      ) : (
        <span className="text-muted">—</span>
      ),
  },
];

export function CreditTransactionsProjectDetailsPage() {
  const {projectId, studioProjectId, chatId} = useParams<{
    projectId?: string;
    studioProjectId?: string;
    chatId?: string;
  }>();
  const kind: 'site' | 'studio' | 'chat' = chatId
    ? 'chat'
    : studioProjectId
      ? 'studio'
      : 'site';
  const safeProjectId = Number.parseInt(studioProjectId || projectId || '', 10);
  const endpoint =
    kind === 'chat' && chatId
      ? detailsEndpoint(kind, 0, chatId)
      : Number.isInteger(safeProjectId)
        ? detailsEndpoint(kind, safeProjectId)
        : detailsEndpoint(kind, 0);
  const projectMetaQuery = useCreditTransactionsProjectDetailsMeta(
    kind,
    safeProjectId,
    chatId,
  );
  const projectMeta = projectMetaQuery.data?.project;
  const title =
    kind === 'chat'
      ? undefined
      : projectMeta?.name || undefined;

  return (
    <div className="min-h-screen bg-alt">
      <StaticPageTitle>
        {kind === 'chat' ? (
          <Trans message="Chat" />
        ) : title ? (
          title
        ) : (
          <Trans message="Project transaction details" />
        )}
      </StaticPageTitle>
      <UserCabinetNavbar />
      <div className="container mx-auto px-24 py-24">
        <div className="mb-24">
          <Button
            variant="text"
            color="primary"
            size="sm"
            startIcon={<ArrowBackIcon />}
            elementType={Link}
            to="/account-settings/credits/transactions"
          >
            <Trans message="Back to credits by project" />
          </Button>
        </div>
        <h1 className="mb-12 text-3xl">
          {kind === 'chat' ? (
            <Trans message="Chat" />
          ) : (
            title || <Trans message="Project transaction details" />
          )}
        </h1>
        {kind === 'studio' ? (
          <p className="mb-12 text-sm text-muted">
            <Trans message="Studio" />
          </p>
        ) : null}
        {kind === 'chat' && chatId ? (
          <p className="mb-12 font-mono text-sm text-muted">{chatId}</p>
        ) : null}
        {projectMeta?.url ? (
          <p className="mb-12 text-sm">
            <a
              href={projectMeta.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {projectMeta.url}
            </a>
          </p>
        ) : null}
        {projectMeta?.deleted ? (
          <p className="mb-12 text-sm text-muted">
            <Trans message="Project deleted" />
          </p>
        ) : null}
        <p className="mb-12 text-sm text-muted">
          <Trans message="Detailed entries are available for chats. If a chat was deleted, its entries are removed from this breakdown." />
        </p>
        <LearnMoreLink
          link={AI_TOKENS_AND_CACHE_DOCS_HREF}
          className="mb-20"
        />
        <DataTable<CreditTransactionDetailRow>
          endpoint={endpoint}
          columns={columns}
          hideSearch
          enableSelection={false}
          collapseTableOnMobile={false}
          emptyStateMessage={<Trans message="No project transactions yet." />}
        />
      </div>
    </div>
  );
}
